<template>
  <div
    class="modal generic-geofence-search-modal"
    :class="className">
    <div
      class="modal-dialog"
      :class="{ 'modal-full-screen': isFullscreen }"
      role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ capitalize($t('messages.chooseGenericGeofences')) }}</h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
            @click="cancel">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body flex-v">
          <div class="modal-section-separator">
            <div class="line" />
            <div class="text">
              {{ capitalize($t('actions.search')) }}
            </div>
            <div class="line" />
          </div>

          <div class="place-attributes">
            <div class="place-attribute">
              <div class="attribute-group-title">{{ $t('names.country', { n: 1 }) }}:</div>
              <multiselect
                v-model="selectedCountry"
                track-by="id"
                label="name"
                :options="countryLocations"
                :searchable="false"
                :allow-empty="false"
                :show-labels="false"
                :close-on-select="true"
                :multiple="false"
                :placeholder="capitalize($t('messages.pickACountry'))" />
            </div>
            <div class="place-attribute">
              <div class="attribute-group-title">{{ $t('names.type', { n: 1 }) }}:</div>
              <multiselect
                v-model="selectedGeofenceType"
                track-by="geofenceType"
                label="name"
                :options="countryTypes"
                :searchable="false"
                :allow-empty="false"
                :show-labels="false"
                :close-on-select="true"
                :multiple="false"
                :disabled="selectedCountry === null"
                :placeholder="capitalize($t('messages.pickAType'))" />
            </div>
            <div class="place-attribute">
              <div class="attribute-group-title">{{ $t('names.name', { n: 2 }) }}:</div>
              <textarea
                v-model="searchRaw"
                v-tooltip.top="searchInputTooltip"
                class="form-control"
                rows="1"
                cols="50"
                placeholder="e.g.: 90210,10001"
                :disabled="selectedGeofenceType === null" />
              <a
                v-if="selectedGeofenceType?.geofenceType === 'county'"
                href="/assets/files/us-counties.csv">
                {{ i18n.t('messages.downloadListOfUSCounties') }}
              </a>
            </div>
            <div class="place-attribute">
              <div class="attribute-group-title">&nbsp;</div>
              <button
                class="btn btn-primary btn-sm"
                @click="search">
                {{ $t('actions.search') }}
              </button>
            </div>
          </div>
          <geofence-list-map-selector
            ref="geofenceListMapSelector"
            :geofences="geofences"
            :requested-for-utc="requestedForUtc"
            @delete-geofence="handleDeleteGeofence"
            @select-geofence="handleSelectGeofence"
            @unselect-geofence="handleUnselectGeofence" />
        </div>
        <div class="modal-footer">
          <button
            id="add_geofences_btn"
            type="button"
            class="btn btn-primary"
            @click="confirm">
            {{ $t('actions.ok') }}
          </button>
          <button
            type="button"
            class="btn btn-default"
            data-dismiss="modal"
            @click="cancel">
            {{ $t('actions.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, ref, useTemplateRef, watch } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';
  import { useI18n } from 'vue-i18n';
  import Multiselect from 'vue-multiselect';

  import GeofenceListMapSelector from '@/common/components/controls/GeofenceListMapSelector.vue';
  import {
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    GenericGeofenceTypeCountry,
    QueryParams,
  } from '@/common/models';
  import Geofence from '@/common/models/geo/Geofence';
  import GeofencesUtils from '@/org-app/components/geofenceGroups/Geofences.utils';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';

  const i18n = useI18n();

  interface IProps {
    id: string;
    isVisible: boolean;
    isFullscreen: boolean;
    requestedForUtc: DateTime;
  }

  const props = defineProps<IProps>();

  interface IEmits {
    (event: 'confirmed', geofences): void;
    (event: 'cancelled'): void;
  }

  const emits = defineEmits<IEmits>();

  const countryLocations = ref([]);
  const geofences = ref<Geofence[]>([]);
  const searchRaw = ref('');
  const selectedCountry = ref(null);
  const selectedGeofenceType = ref<GenericGeofenceTypeCountry>(null);

  const geofenceListMapSelector =
    useTemplateRef<ComponentExposed<typeof GeofenceListMapSelector>>('geofenceListMapSelector');

  const { GeofenceGroups } = mapOrgAppState();

  const genericGeofenceGroupTypeCountries = computed(() => GeofenceGroups.genericGeofenceGroupTypeCountries);

  const className = computed(() => {
    if (props.isVisible) {
      return 'visible';
    }
    return 'fade';
  });

  const countryTypes = computed(() => {
    if (!selectedCountry.value) {
      return [];
    }
    const types = genericGeofenceGroupTypeCountries.value.filter((x) => x.countryCode === selectedCountry.value.code);
    if (types.length === 1) {
      [selectedGeofenceType.value] = types;
    }
    return types;
  });

  const searchInputTooltip = computed(() => {
    return selectedGeofenceType.value?.geofenceType === 'county'
      ? i18n.t('messages.usCountySearchTooltip')
      : i18n.t('messages.namesSeparatedByCommaNewline');
  });

  const { getGenericGeofences, getLocations } = mapOrgAppActions();

  function confirm() {
    emits(
      'confirmed',
      geofences.value.filter((x) => x.isSelected),
    );
  }

  function cancel() {
    emits('cancelled');
  }

  async function search() {
    const names = GeofencesUtils.splitGenericGeofenceNames(
      searchRaw.value,
      selectedCountry.value?.code,
      selectedGeofenceType.value?.geofenceType,
    );
    const queryParams = QueryParams.for({
      top: null,
      filterCollection: new FilterCollection([
        new Filter('name', names, FilterComparisonOperator.IN),
        new Filter('country_code', [selectedCountry.value.code]),
        new Filter('geofence_type', [selectedGeofenceType.value.geofenceType]),
      ]),
    });
    const genericGeofences = (await getGenericGeofences({ queryParams, showLoader: true })).items;
    if (genericGeofences && genericGeofences.length > 0) {
      const existingIDs = geofences.value.map((g) => g.id);
      genericGeofences.forEach((g) => {
        if (existingIDs.indexOf(g.id) === -1) {
          geofences.value.push(g);
        }
      });
      geofenceListMapSelector.value.refresh();
    }
  }

  function handleDeleteGeofence(g) {
    geofences.value = geofences.value.filter((x) => x.id !== g.id);
    geofenceListMapSelector.value.refresh();
  }

  function handleSelectGeofence(g) {
    const geofence = geofences.value.find((x) => x.id === g.id);
    if (geofence) {
      geofence.isSelected = true;
    }
    geofenceListMapSelector.value.refresh();
  }

  function handleUnselectGeofence(g) {
    const geofence = geofences.value.find((x) => x.id === g.id);
    if (geofence) {
      geofence.isSelected = false;
    }
    geofenceListMapSelector.value.refresh();
  }

  async function getCountries() {
    const countryCodes = genericGeofenceGroupTypeCountries.value.map((x) => x.countryCode);
    if (countryCodes.length === 0) {
      return;
    }
    countryLocations.value = await getLocations(
      QueryParams.for({
        top: null,
        filterCollection: new FilterCollection([
          new Filter('level', [0]),
          new Filter('code', countryCodes, FilterComparisonOperator.IN),
        ]),
      }),
    );
    if (countryLocations.value.length === 1) {
      [selectedCountry.value] = countryLocations.value;
    }
  }

  onMounted(() => {
    getCountries();
  });

  watch(genericGeofenceGroupTypeCountries, () => {
    getCountries();
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .place-attributes {
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;
    margin-bottom: 1rem;

    .place-attribute {
      display: flex;
      flex-direction: column;
      margin-right: 0.8rem;

      .multiselect {
        min-width: 20rem;
      }

      .attribute-group-title {
        text-transform: uppercase;
        font-size: 0.9em;
        margin-bottom: 0.3rem;
        font-weight: 500;
      }

      .text-search-input {
        width: 21rem;
      }
    }
  }
</style>
